import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Drawer, Space, Popconfirm, Card, Tooltip, Input } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import AddService from "./AddService";
import { ServicetypeService } from "../../../Services/Manage/ServicetypeService";
import useMessage from "antd/es/message/useMessage";
import dayjs from "dayjs";

const getPermission = (link) => {
  try {
    const raw = localStorage.getItem("ome_permission");
    const parsed = JSON.parse(raw);
    const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
    return permissions.find((p) => p.link === link) || {
      add: false,
      edit: false,
      delete: false,
      read: false,
    };
  } catch {
    return { add: false, edit: false, delete: false, read: false };
  }
};

const ViewServices = () => {
  const perm = getPermission("/manage-services");
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [message, msgcontxt] = useMessage();

  const getAllServices = async () => {
    try {
      const request = await ServicetypeService.getAllServices();

      const sortedData = [...(request.data || [])]
        .sort((a, b) => Number(a.id || 0) - Number(b.id || 0))
        .map((item, index) => ({
          ...item,
          key: item.id,
          si_no: index + 1,
        }));

      setData(sortedData);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getAllServices();
  }, []);

  const filteredData = useMemo(() => {
    const search = searchText.toLowerCase().trim();

    if (!search) return data;

    return data.filter((item) =>
      [
        item.service_name,
        item.service_code,
        item.unit_price,
        item.status,
        item.note,
        item.added_by,
      ]
        .join(" ")
        .toLowerCase()
        .includes(search)
    );
  }, [data, searchText]);

  const handleAdd = () => {
    setEditingRecord(null);
    setDrawerOpen(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setDrawerOpen(true);
  };

  const handleSubmit = () => {
    setDrawerOpen(false);
    getAllServices();
  };

  const handleDelete = async (id) => {
    try {
      const req = await ServicetypeService.deleteServices(id);

      if (req.status) {
        message.success(req.message);
        getAllServices();
      } else {
        message.error(req.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const columns = [
    { title: "S. No", dataIndex: "si_no", key: "si_no" },
    { title: "Service Name", dataIndex: "service_name", key: "service_name" },
    { title: "Service Code", dataIndex: "service_code", key: "service_code" },
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      render: (value) => value || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <span
          style={{
            color: value === "Active" ? "#52c41a" : "#ff4d4f",
            fontWeight: 600,
          }}
        >
          {value || "Active"}
        </span>
      ),
    },
    {
  title: "Note",
  dataIndex: "note",
  key: "note",
  width: 180,
  render: (value) => (
    <Tooltip title={value || "-"}>
      <div
        style={{
          maxWidth: 180,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          cursor: "pointer",
        }}
      >
        {value || "-"}
      </div>
    </Tooltip>
  ),
},
    { title: "Added By", dataIndex: "added_by", key: "added_by" },
    {
      title: "Updated On",
      dataIndex: "updated_on",
      key: "updated_on",
      render: (value) => (value ? dayjs(value).format("DD-MM-YYYY hh:mm A") : "-"),
    },
    {
      title: "Added On",
      dataIndex: "added_on",
      key: "added_on",
      render: (value) => (value ? dayjs(value).format("DD-MM-YYYY hh:mm A") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
            <Button
              size="large"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={!perm.edit}
            />
          </Tooltip>

          <Popconfirm
            title="Are you sure to delete?"
            onConfirm={() => handleDelete(record.id)}
            disabled={!perm.delete}
          >
            <Tooltip title={!perm.delete ? "You don't have permission to delete" : ""}>
              <Button size="large" danger icon={<DeleteOutlined />} disabled={!perm.delete} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Manage Services"
      extra={
        <Space>
          <Input
            size="large"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search services..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 260 }}
          />

          <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
            <Button
              size="large"
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={!perm.add}
            >
              Add Service
            </Button>
          </Tooltip>
        </Space>
      }
    >
      {msgcontxt}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredData}
        pagination
        scroll={{ x: "max-content" }}
      />

      <Drawer
        title={editingRecord ? "Edit Service" : "Add Service"}
        width={400}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        destroyOnClose
      >
        <AddService
          initialValues={editingRecord}
          onSubmit={handleSubmit}
          onCancel={() => setDrawerOpen(false)}
        />
      </Drawer>
    </Card>
  );
};

export default ViewServices;